// Catalog.ID Client — Pane Type Registration
// ============================================================================

import {
    Shell,
    createGridSurfacePaneType,
    createFlowSurfacePaneType,
} from '@catalog-shell';
import type { ActionDefinition, ShellAPI } from '@catalog-shell';
import { identity } from './state/identity';
import { logOutEverywhere } from './auth/logout-action';
import { PANE_IDS } from './shell-config';

// Phase 8c — Claims
import { createClaimsListPane } from './pane-types/claims-list-pane';
import { createClaimDetailPane } from './pane-types/claim-detail-pane';
import { createSharingPane } from './pane-types/sharing-pane';
import { createSharedWithMePane, createSharedClaimViewPane } from './pane-types/shared-with-me-pane';

// Phase 8d — Public Claims & Profiles
import { createPublicClaimsPane } from './pane-types/public-claims-pane';
import { createPublicClaimDetailPane } from './pane-types/public-claim-detail-pane';
import { createPublicProfilePane } from './pane-types/public-profile-pane';
import { createOperatorReviewPane, createOperatorReviewDetailPane } from './pane-types/operator-review-pane';
import { createUnlockRequestsPane, createUnlockRequestDetailPane } from './pane-types/unlock-requests-pane';

// Phase 8e — Organizations & Delegates
import { createDelegatesListPane } from './pane-types/delegates-list-pane';
import { createDelegateDetailPane } from './pane-types/delegate-detail-pane';
import { createOrgClaimsPane } from './pane-types/org-claims-pane';
import { createOrgRecoveryPane } from './pane-types/org-recovery-pane';
import { createOrgVouchersPane } from './pane-types/org-vouchers-pane';
import { createOrgWalletsPane } from './pane-types/org-wallets-pane';

// Phase 8g — OIDC. The consent screen has no pane type: its two live surfaces
// are the hub-relayed modal (`showOidcConsentModal`, key-grant/client.ts) and
// the browser route `/oidc/consent` (`renderOidcConsentStandalone`, app.ts).
import { createVouchersPane } from './pane-types/vouchers-pane';
import { createConnectedAppsPane } from './pane-types/connected-apps-pane';

// Dashboard-module-only: Connected Modules
import { createConnectedModulesPane } from './pane-types/connected-modules-pane';
import { isDashboardContext } from './state/dashboard-context';

// Phase 8h — Account Management & Settings
import { createDashboardPane } from './pane-types/dashboard-pane';
import { createSettingsPane } from './pane-types/settings-pane';
import { createDevicesPane } from './pane-types/devices-pane';

// Chat migration §14 M3 — account chat-root publication ceremony
import { createChatRootPane } from './pane-types/chat-root-pane';

// Phase 8f — Agent Management
import { createAgentsListPane } from './pane-types/agents-list-pane';
import { createAgentDetailPane } from './pane-types/agent-detail-pane';
import { createAgentApiKeysPane } from './pane-types/agent-api-keys-pane';
import { createAgentTokensPane } from './pane-types/agent-tokens-pane';

// Phase 9 — Trust Network
import { createTrustGivenPane } from './pane-types/trust-given-pane';
import { createTrustDetailPane } from './pane-types/trust-detail-pane';
import { createTrustReceivedPane } from './pane-types/trust-received-pane';
import { createTrustExpressPane } from './pane-types/trust-express-pane';

// Operator↔org binding ceremony (docs/operator-org-binding.md §6 Q1)
import { createCeremonyConsolePane } from './pane-types/ceremony-console-pane';

// Phase 11 — Registry integration (per-machine signing keys)

// ============================================================================
// Module-level actions (top bar + gear menu)
//
// These three were the dashboard pane's "Quick Actions" group. Not one of them
// is about the dashboard's subject — they create a claim, look somebody up, and
// start an organization registration — so they belong to the module, and the
// module's place for a verb is the top bar (shell USAGE, "Where a button
// belongs"). None of them may be gated on the focused pane; the one predicate
// here reads the ACTIVE IDENTITY TYPE, which is module state and therefore
// fair game.
//
// Order in the bar is registration order after the identity picker (app.ts):
// identity picker, + New Claim, Look Up Profile.
// ============================================================================

/** Open the new-claim form on the claims workspace, beside the claims list. */
function openNewClaim(shell: ShellAPI): void {
    shell.setActiveWorkspace('claims');
    const surface = shell.getRootSurface();
    if (!surface) return;
    // The claims workspace is a flow surface whose first pane is the list;
    // naming it as the origin puts the form right after it, exactly where the
    // list's own "+ New Claim" puts it. Absent (a persisted layout that closed
    // it), the form still opens — at the end of the flow.
    const list = surface.getPanes().find(p => p.typeId === PANE_IDS.claimsList);
    surface.openPane(
        { typeId: PANE_IDS.claimDetail, title: 'New Claim', context: { mode: 'new' } },
        list ? { originPaneId: list.instanceId } : undefined,
    );
}

/** Open the public-profile lookup on the home workspace. */
function openProfileLookup(shell: ShellAPI): void {
    shell.setActiveWorkspace('home');
    shell.getRootSurface()?.openPane({
        typeId: PANE_IDS.publicProfile,
        title: 'Profile Lookup',
        context: {},
    });
}

/** The CID module's own verbs, as shell action definitions. */
export function cidModuleActions(): ActionDefinition[] {
    return [
        {
            actionId: 'cid.new-claim',
            label: '+ New Claim',
            locations: ['toolbar'],
            requiredCapabilities: [],
            execute: (ctx) => { openNewClaim(ctx.shell); },
        },
        {
            actionId: 'cid.lookup-profile',
            label: 'Look Up Profile',
            locations: ['toolbar'],
            requiredCapabilities: [],
            execute: (ctx) => { openProfileLookup(ctx.shell); },
        },
        {
            // Rare, and only ever offered to a personal identity — a delegate or
            // an organization cannot found another organization — so it rides
            // the gear menu rather than taking permanent width in the bar.
            actionId: 'cid.register-organization',
            label: 'Register Organization…',
            locations: ['menu'],
            requiredCapabilities: [],
            contextPredicate: () => identity.isPersonal(),
            execute: () => { window.location.href = '/register/organization'; },
        },
        {
            // Ending the session is about the MODULE, not about whichever pane
            // is focused — it used to be a button inside Settings, which is
            // where nobody looks for it and where it competed with "Forget this
            // device". One Log Out, in the gear menu, for every identity.
            actionId: 'cid.log-out',
            label: 'Log Out',
            locations: ['toolbar'],
            requiredCapabilities: [],
            execute: async (ctx) => {
                const ok = await ctx.shell.confirm({
                    title: 'Log Out',
                    message: 'Log out of all identities on this device? This device stays enrolled: '
                        + 'next time your password (and PIN, if set) is enough.',
                    confirmLabel: 'Log Out',
                });
                if (!ok) return;
                await logOutEverywhere();
            },
        },
    ];
}

// ============================================================================
// Registration
// ============================================================================

export function registerPaneTypes(shell: Shell): void {
    for (const action of cidModuleActions()) shell.registerAction(action);

    // Surface types
    shell.registerPaneType(createGridSurfacePaneType());
    shell.registerPaneType(createFlowSurfacePaneType());

    // Home
    shell.registerPaneType(createDashboardPane());
    shell.registerPaneType(createPublicProfilePane());

    // Operator
    shell.registerPaneType(createOperatorReviewPane());
    shell.registerPaneType(createOperatorReviewDetailPane());
    shell.registerPaneType(createUnlockRequestsPane());
    shell.registerPaneType(createUnlockRequestDetailPane());

    // Claims
    shell.registerPaneType(createClaimsListPane());
    shell.registerPaneType(createClaimDetailPane());
    shell.registerPaneType(createSharingPane());
    shell.registerPaneType(createSharedWithMePane());
    shell.registerPaneType(createSharedClaimViewPane());
    shell.registerPaneType(createPublicClaimsPane());
    shell.registerPaneType(createPublicClaimDetailPane());

    // Organization — REAL implementations
    shell.registerPaneType(createDelegatesListPane());
    shell.registerPaneType(createDelegateDetailPane());
    shell.registerPaneType(createOrgClaimsPane());
    shell.registerPaneType(createOrgRecoveryPane());
    // Delegate-voucher ledger (registration-restructure §13.4). Owner/admin —
    // the pane says so itself, and the server gates every call independently.
    shell.registerPaneType(createOrgVouchersPane());
    // The organization's bitcash wallet (registration-wallets §4.13). On every
    // delegate's roster; the pane itself tells anyone but an owner or a
    // treasurer that they do not hold the key.
    shell.registerPaneType(createOrgWalletsPane());

    // Agents — REAL implementations
    shell.registerPaneType(createAgentsListPane());
    shell.registerPaneType(createAgentDetailPane());
    shell.registerPaneType(createAgentApiKeysPane());
    shell.registerPaneType(createAgentTokensPane());

    // Settings — REAL implementations
    shell.registerPaneType(createSettingsPane());
    shell.registerPaneType(createDevicesPane());
    // Chat account root — the once-per-account publication ceremony (chat
    // migration §14 M3). In Settings beside Devices: Devices is the per-machine
    // key surface, this is the per-account one. Also served standalone at the
    // `/chat-root` deep link (app.ts) for the chat first-run trigger.
    shell.registerPaneType(createChatRootPane());
    shell.registerPaneType(createConnectedAppsPane());

    // Connected Modules — gated on dashboard module context. When running as the
    // standalone catalog.id site there are no sibling modules to track or revoke.
    if (isDashboardContext()) {
        shell.registerPaneType(createConnectedModulesPane());
    }

    // Trust
    shell.registerPaneType(createTrustGivenPane());
    shell.registerPaneType(createTrustDetailPane());
    shell.registerPaneType(createTrustReceivedPane());
    shell.registerPaneType(createTrustExpressPane());

    // Operator: registration voucher backoffice. Visibility rides the operator
    // workspace gate in app.ts; the server gates every call independently.
    shell.registerPaneType(createVouchersPane());

    // Ceremony console — registered so the `/ceremony` deep link can also open
    // it inside the shell. Intentionally NOT added to any workspace layout or
    // recommendedPanes list: the payload summons it, nothing links to it.
    shell.registerPaneType(createCeremonyConsolePane());

    // Registry: per-machine registry signing key management + claim publishing
    // is the responsibility of the standalone registry module (Phase 5). Agent
    // registry_signing_key claims are still created via agents-list-pane during
    // agent registration and surface through the standard claim views.
}
